import React, { useState, useEffect } from 'react';
import { Card, Button, Table, Tag, Space, Typography, Alert, Collapse, Badge, Tooltip } from 'antd';
import { BugOutlined, DownloadOutlined, DeleteOutlined, ReloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import errorLogger from '../utils/errorLogger';

const { Title, Text } = Typography;
const { Panel } = Collapse;

interface ErrorInfo {
  timestamp: string;
  type: 'error' | 'warning' | 'info';
  message: string;
  stack?: string;
  url?: string;
  userAgent?: string;
  userId?: string;
  component?: string;
  props?: any;
  state?: any;
  error?: any;
}

const ErrorMonitor: React.FC = () => {
  const [errors, setErrors] = useState<ErrorInfo[]>([]);
  const [stats, setStats] = useState<any>(null);

  const refreshData = () => {
    const errorList = errorLogger.getErrors();
    const errorStats = errorLogger.getErrorStats();
    setErrors(errorList);
    setStats(errorStats);
  };

  useEffect(() => {
    refreshData();
    // 每5秒刷新一次数据
    const interval = setInterval(refreshData, 5000);
    return () => clearInterval(interval);
  }, []);

  const columns = [
    {
      title: '时间',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 180,
      render: (timestamp: string) => new Date(timestamp).toLocaleString(),
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 80,
      render: (type: string) => {
        const color = type === 'error' ? 'red' : type === 'warning' ? 'orange' : 'blue';
        return <Tag color={color}>{type.toUpperCase()}</Tag>;
      },
    },
    {
      title: '组件',
      dataIndex: 'component',
      key: 'component',
      width: 120,
      render: (component: string) => component || '-',
    },
    {
      title: '消息',
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
      render: (message: string, record: ErrorInfo) => (
        <Tooltip title={message}>
          <Text code style={{ maxWidth: '200px', display: 'block' }}>
            {message}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_: any, record: ErrorInfo) => (
        <Space>
          <Button 
            size="small" 
            icon={<InfoCircleOutlined />}
            onClick={() => {
              console.log('错误详情:', record);
              alert(`错误详情已输出到控制台\n\n消息: ${record.message}\n组件: ${record.component || '未知'}\n时间: ${new Date(record.timestamp).toLocaleString()}`);
            }}
          >
            详情
          </Button>
        </Space>
      ),
    },
  ];

  const handleExport = () => {
    errorLogger.exportErrors();
  };

  const handleClear = () => {
    errorLogger.clearErrors();
    refreshData();
  };

  const handlePrintSummary = () => {
    errorLogger.printSummary();
  };

  const handleTestError = () => {
    // 测试错误记录功能
    try {
      throw new Error('这是一个测试错误，用于验证错误日志系统是否正常工作');
    } catch (error) {
      errorLogger.logError(error, 'ErrorMonitor', { test: true });
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <Card>
        <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={3} style={{ margin: 0 }}>
              <BugOutlined /> 错误监控面板
            </Title>
            <Text type="secondary">实时监控应用错误和警告</Text>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={refreshData}>
              刷新
            </Button>
            <Button icon={<DownloadOutlined />} onClick={handleExport}>
              导出日志
            </Button>
            <Button icon={<DeleteOutlined />} onClick={handleClear} danger>
              清空日志
            </Button>
            <Button onClick={handleTestError} type="dashed">
              测试错误
            </Button>
          </Space>
        </div>

        {stats && (
          <Alert
            message={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Badge count={stats.errors} style={{ backgroundColor: '#ff4d4f' }} />
                  <span style={{ marginLeft: '8px', marginRight: '16px' }}>错误</span>
                  <Badge count={stats.warnings} style={{ backgroundColor: '#faad14' }} />
                  <span style={{ marginLeft: '8px', marginRight: '16px' }}>警告</span>
                  <Badge count={stats.info} style={{ backgroundColor: '#1890ff' }} />
                  <span style={{ marginLeft: '8px' }}>信息</span>
                </div>
                <div>
                  <Text type="secondary">总计: {stats.total} 条记录</Text>
                </div>
              </div>
            }
            type="info"
            style={{ marginBottom: '20px' }}
            action={
              <Button size="small" onClick={handlePrintSummary}>
                打印摘要到控制台
              </Button>
            }
          />
        )}

        <Table
          columns={columns}
          dataSource={errors}
          rowKey={(record, index) => `${record.timestamp}-${index}`}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          }}
          size="small"
          scroll={{ x: 800 }}
        />

        <Collapse style={{ marginTop: '20px' }}>
          <Panel header="最近错误详情" key="1">
            {stats?.recent?.map((error: ErrorInfo, index: number) => (
              <Card key={index} size="small" style={{ marginBottom: '10px' }}>
                <div style={{ marginBottom: '8px' }}>
                  <Tag color={error.type === 'error' ? 'red' : error.type === 'warning' ? 'orange' : 'blue'}>
                    {error.type.toUpperCase()}
                  </Tag>
                  <Text strong>{error.component || '未知组件'}</Text>
                  <Text type="secondary" style={{ marginLeft: '10px' }}>
                    {new Date(error.timestamp).toLocaleString()}
                  </Text>
                </div>
                <Text code>{error.message}</Text>
                {error.stack && (
                  <pre style={{ 
                    background: '#f5f5f5', 
                    padding: '8px', 
                    borderRadius: '4px',
                    marginTop: '8px',
                    fontSize: '12px',
                    maxHeight: '100px',
                    overflow: 'auto'
                  }}>
                    {error.stack}
                  </pre>
                )}
              </Card>
            ))}
          </Panel>
        </Collapse>
      </Card>
    </div>
  );
};

export default ErrorMonitor;
